import { useDropzone } from "react-dropzone";
import React, { useCallback, useEffect, useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";

interface IFileDropzoneProps {
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  allowMultipleFiles: boolean;
  files: any;
}

const FileDropzone: React.FC<IFileDropzoneProps> = ({
  setFiles,
  allowMultipleFiles,
  files,
}) => {
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles?.length) return;

      if (allowMultipleFiles) {
        setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
        setFiles((prev) => [...prev, ...acceptedFiles]);
      } else {
        const first = acceptedFiles[0];
        setUploadedFiles(first ? [first] : []);
        setFiles(first ? [first] : []);
      }
    },
    [setFiles, allowMultipleFiles],
  );

  useEffect(() => {
    if (!allowMultipleFiles && uploadedFiles.length > 1) {
      const first = uploadedFiles[0];
      setUploadedFiles(first ? [first] : []);
      setFiles(first ? [first] : []);
    }
  }, [allowMultipleFiles, uploadedFiles, setFiles]);

  useEffect(() => {
    setUploadedFiles(files);
  }, [files]);

  const handleRemoveFile = (file: File) => {
    setUploadedFiles((prev) => prev.filter((f) => f !== file));
    setFiles((prev) => prev.filter((f) => f !== file));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/*": [".txt"],
    },
    multiple: allowMultipleFiles,
  });

  return (
    <div className="mt-1">
      <div
        {...getRootProps()}
        className="border border-dashed border-gray-400 p-3 text-center rounded-md bg-gray-300 dark:bg-gray-900 text-black dark:text-white cursor-pointer"
      >
        <input {...getInputProps()} />
        <p className="mb-2">{t("adminPanel.fileDropFileTitle")}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("adminPanel.acceptedFiles")} .docx, .doc, .xlsx, .txt, .pdf.
        </p>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="mt-4 max-h-52 overflow-y-auto">
          <ul>
            {uploadedFiles.map((file, index) => (
              <li
                key={index}
                className="flex items-center justify-between mb-2 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded"
              >
                <span className="text-sm text-base-color">{file.name}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(file)}
                  className="text-del-red hover:text-red-700"
                >
                  <FaTrashAlt />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export { FileDropzone };
